import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
// Component imports
import PersonalInformation from './profile_sections/Personalinfo';
import HospitalContactInfo from './profile_sections/Contactsinfo';
import HospitalAccreditations from './profile_sections/Accreditations';
import StaffInfo from './profile_sections/Employees';
import FacilitiesEquipment from './profile_sections/Facilities';
import SocialMediaInfo from './profile_sections/SocialAccounts';
import ProfileSummary from './profile_sections/Summary';
import { showToast, toTitleCase } from '../../../utils/config/helpers';
import { toast } from 'react-toastify';
import { updateHospitalData } from '../../../redux/middleware/user/hospital/hospitaldata';

const tabs = [
	{ id: 'personal', label: 'Personal Info' },
	{ id: 'contact', label: 'Contact Info' },
	{ id: 'accreditation', label: 'Accreditation' },
	{ id: 'employees', label: 'Employees' },
	{ id: 'facilities', label: 'Facilities' },
	{ id: 'socialAccounts', label: 'Social Accounts' },
	{ id: 'summary', label: 'Summary' },
];

const HospitalProfile = ({ preFilledData }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { credentials } = useSelector((state) => state.hospitalAuth);
	const { loading, error } = useSelector((state) => state.hospitalData);
	const [activeTab, setActiveTab] = useState('personal');
	const [formData, setFormData] = useState({
		personalInfo: { ...preFilledData },
		contactInfo: { ...preFilledData },
		accreditation: { ...preFilledData },
		employees: { ...preFilledData },
		facilities: { ...preFilledData },
		socialAccounts: { ...preFilledData },
	});
	const [sectionStatus, setSectionStatus] = useState({
		personal: { isComplete: false, hasError: false },
		contact: { isComplete: false, hasError: false },
		accreditation: { isComplete: false, hasError: false },
		employees: { isComplete: false, hasError: false },
		facilities: { isComplete: false, hasError: false },
		socialAccounts: { isComplete: false, hasError: false },
		summary: { isComplete: false, hasError: false },
	});
	const [completionPercentage, setCompletionPercentage] = useState(0);
	const tabsRef = useRef(null);

	useEffect(() => {
		if (updateHospitalData) {
			setFormData(updateHospitalData);
		}
	}, [updateHospitalData]);

	const calculateCompletionPercentage = useCallback(() => {
		const requiredSections = Object.keys(sectionStatus);
		const completedSections = requiredSections.filter(
			(section) =>
				sectionStatus[section].isComplete && !sectionStatus[section].hasError,
		).length;
		setCompletionPercentage(
			(completedSections / requiredSections.length) * 100,
		);
	}, [sectionStatus]);

	useEffect(() => {
		calculateCompletionPercentage();
	}, [calculateCompletionPercentage]);

	const scrollToTab = (tabIndex) => {
		tabsRef.current[tabIndex]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'center',
		});
	};

	useEffect(() => {
		scrollToTab(activeTab);
	}, [activeTab, scrollToTab]);

	const moveToNextSection = useCallback(() => {
		setActiveTab((prevTab) => {
			const currentIndex = tabs.findIndex((tab) => tab.id === prevTab);
			if (currentIndex < tabs.length - 1) {
				return tabs[currentIndex + 1].id;
			}
			return prevTab;
		});
	}, []);

	const moveToPreviousSection = () => {
		setActiveTab((activeTab) => {
			const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
			if (currentIndex > 0) {
				return tabs[currentIndex - 1].id; // Return the new active tab id
			}
			return activeTab; // Return the current active tab if there's no previous tab
		});
	};

	const handleSectionComplete = (sectionId, isComplete, hasError = false) => {
		setSectionStatus((prevStatus) => ({
			...prevStatus,
			[sectionId]: { isComplete, hasError },
		}));
	};

	useEffect(() => {
		if (
			sectionStatus[activeTab]?.isComplete &&
			!sectionStatus[activeTab]?.hasError
		) {
			moveToNextSection();
		}
	}, [sectionStatus, activeTab, moveToNextSection]);

	const isProfileComplete = useCallback(() => {
		const requiredSections = [
			'personal',
			'contact',
			'accreditation',
			'employees',
			'facilities',
			'socialAccounts',
		];
		return requiredSections.every(
			(section) =>
				sectionStatus[section].isComplete && !sectionStatus[section].hasError,
		);
	}, [sectionStatus]);

	const canNavigateToSection = (tabId) => {
		const tabIndex = tabs.findIndex((tab) => tab.id === tabId);
		return tabs
			.slice(0, tabIndex)
			.every(
				(tab) =>
					sectionStatus[tab.id].isComplete && !sectionStatus[tab.id].hasError,
			);
	};

	const handleSubmit = async () => {
		e.preventDefault();
		console.log('Profile submitted:', formData);
		try {
			const response = await dispatch(updateHospitalData(formData));
			if (response?.payload?.status === 200) {
				showToast(toast, 'success', 'Profile updated successfully');
				navigate('/app/hospital/dashboard');
			}
		} catch (error) {
			showToast(toast, 'error', 'Failed to update profile');
		}
	};

	return (
		<div className={css(styles.container)}>
			<div className={css(styles.contentWrapper)}>
				<h1 className={css(styles.title)}>Complete Your Profile</h1>
				<div className={css(styles.progressBarContainer)}>
					<motion.div
						className={css(styles.progressBar)}
						initial={{ width: 0 }}
						animate={{ width: `${completionPercentage}%` }}
						transition={{ duration: 0.5 }}
					/>
				</div>
				<p className={css(styles.progressText)}>
					{`${Math.round(completionPercentage)}% Complete`}
				</p>

				<div className={css(styles.tabContainer)} ref={tabsRef}>
					{tabs.map((tab, index) => (
						<motion.button
							key={tab.id}
							id={`tab-${tab.id}`}
							className={css(
								styles.tabButton,
								activeTab === tab.id && styles.activeTab,
								sectionStatus[tab.id].isComplete &&
									!sectionStatus[tab.id].hasError &&
									styles.completedTab,
								!canNavigateToSection(tab.id) && styles.disabledTab,
							)}
							onClick={() =>
								canNavigateToSection(tab.id) && setActiveTab(tab.id)
							}
							whileHover={canNavigateToSection(tab.id) ? { scale: 1.05 } : {}}
							whileTap={canNavigateToSection(tab.id) ? { scale: 0.95 } : {}}
							disabled={!canNavigateToSection(tab.id)}
						>
							<span className={css(styles.tabNumber)}>{index + 1}</span>
							<span className={css(styles.tabLabel)}>{tab.label}</span>
							{sectionStatus[tab.id].isComplete &&
								!sectionStatus[tab.id].hasError && (
									<CheckCircle className={css(styles.completedIcon)} />
								)}
							{sectionStatus[tab.id].hasError && (
								<Error className={css(styles.errorIcon)} />
							)}
							{activeTab === tab.id && (
								<motion.div
									className={css(styles.activeTabIndicator)}
									layoutId="activeTabIndicator"
								/>
							)}
						</motion.button>
					))}
				</div>

				<AnimatePresence mode="wait">
					<motion.div
						key={activeTab}
						className={css(styles.tabContent)}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -20 }}
						transition={{ duration: 0.3 }}
					>
						{activeTab === 'personal' && (
							<PersonalInformation
								initialData={formData.personalInfo}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'personal',
										'personalInfo',
										data,
										isValid,
									);
								}}
							/>
						)}
						{activeTab === 'contact' && (
							<HospitalContactInfo
								initialData={formData.contactInfo}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'contact',
										'contactInfo',
										data,
										isValid,
									);
								}}
								onBack={moveToPreviousSection}
							/>
						)}
						{activeTab === 'accreditation' && (
							<HospitalAccreditations
								initialData={formData.accreditation}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'accreditation',
										'accreditation',
										data,
										isValid,
									);
								}}
								onBack={moveToPreviousSection}
							/>
						)}
						{activeTab === 'employees' && (
							<StaffInfo
								initialData={formData.employees}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'employees',
										'employees',
										data,
										isValid,
									);
								}}
								onBack={moveToPreviousSection}
							/>
						)}
						{activeTab === 'facilities' && (
							<FacilitiesEquipment
								initialData={formData.facilities}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'facilities',
										'facilities',
										data,
										isValid,
									);
								}}
								onBack={moveToPreviousSection}
							/>
						)}
						{activeTab === 'socialAccounts' && (
							<SocialMediaInfo
								initialData={formData.socialAccounts}
								onComplete={(data, isValid) => {
									handleSectionComplete(
										'socialAccounts',
										'socialAccounts',
										data,
										isValid,
									);
								}}
								onBack={moveToPreviousSection}
							/>
						)}
						{activeTab === 'summary' && (
							<ProfileSummary
								formData={formData}
								onBack={moveToPreviousSection}
								onSubmit={handleSubmit}
							/>
						)}
					</motion.div>
				</AnimatePresence>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	container: {
		minHeight: '100vh',
		width: '100vw',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: '#f0f4f8',
		padding: '1rem',
		boxSizing: 'border-box',
	},
	contentWrapper: {
		width: '100%',
		maxWidth: '800px',
		backgroundColor: '#ffffff',
		borderRadius: '16px',
		boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
		overflow: 'hidden',
	},
	title: {
		fontSize: '2rem',
		textAlign: 'center',
		margin: '2rem 0',
		color: '#2783F7',
		fontWeight: 'bold',
	},
	progressBarContainer: {
		height: '6px',
		backgroundColor: '#e2e8f0',
		borderRadius: '3px',
		overflow: 'hidden',
		margin: '0 2rem 1rem',
	},
	progressBar: {
		height: '100%',
		backgroundColor: '#2783F7',
		borderRadius: '3px',
		transition: 'width 0.5s ease-in-out',
	},
	progressText: {
		textAlign: 'center',
		marginBottom: '1.5rem',
		fontSize: '0.875rem',
		color: '#4a5568',
		fontWeight: '500',
	},
	tabContainer: {
		display: 'flex',
		overflowX: 'auto',
		scrollbarWidth: 'none',
		msOverflowStyle: 'none',
		'::-webkit-scrollbar': {
			display: 'none',
		},
		padding: '0.5rem 1rem',
		margin: '0 1rem 1.5rem',
		backgroundColor: '#f8fafc',
		borderRadius: '8px',
	},
	tabButton: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: '0.75rem 1rem',
		backgroundColor: 'transparent',
		border: 'none',
		borderRadius: '8px',
		cursor: 'pointer',
		fontSize: '0.875rem',
		color: '#4a5568',
		transition: 'all 0.2s ease-in-out',
		position: 'relative',
		flexShrink: 0,
		':hover': {
			backgroundColor: 'rgba(39, 131, 247, 0.1)',
		},
	},
	activeTab: {
		color: '#2783F7',
		fontWeight: '600',
		backgroundColor: 'rgba(39, 131, 247, 0.1)',
	},
	completedTab: {
		color: '#48bb78',
	},
	disabledTab: {
		opacity: 0.5,
		cursor: 'not-allowed',
	},
	tabNumber: {
		fontSize: '1rem',
		fontWeight: 'bold',
		marginBottom: '0.25rem',
	},
	tabLabel: {
		fontSize: '0.75rem',
		whiteSpace: 'nowrap',
	},
	activeTabIndicator: {
		position: 'absolute',
		bottom: '-0.5rem',
		left: '0',
		right: '0',
		height: '3px',
		backgroundColor: '#2783F7',
		borderRadius: '1.5px',
	},
	completedIcon: {
		position: 'absolute',
		top: '0.25rem',
		right: '0.25rem',
		color: '#48bb78',
		fontSize: '1rem',
	},
	errorIcon: {
		position: 'absolute',
		top: '0.25rem',
		right: '0.25rem',
		color: '#e53e3e',
		fontSize: '1rem',
	},
	tabContent: {
		backgroundColor: '#ffffff',
		borderRadius: '12px',
		padding: '1.5rem',
		margin: '0 1rem 1.5rem',
		boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)',
	},
	submitButton: {
		backgroundColor: '#2783F7',
		color: 'white',
		border: 'none',
		padding: '0.75rem 1rem',
		fontSize: '1rem',
		fontWeight: '600',
		borderRadius: '8px',
		cursor: 'pointer',
		transition: 'all 0.2s ease-in-out',
		boxShadow: '0 4px 6px rgba(39, 131, 247, 0.2)',
		flex: '0 0 auto',
		width: '100%',
		':hover': {
			backgroundColor: '#1c6ed8',
			transform: 'translateY(-2px)',
			boxShadow: '0 6px 8px rgba(39, 131, 247, 0.3)',
		},
	},
	disabledButton: {
		backgroundColor: '#a0aec0',
		cursor: 'not-allowed',
		':hover': {
			backgroundColor: '#a0aec0',
			transform: 'none',
			boxShadow: 'none',
		},
	},
	'@media (max-width: 768px)': {
		container: {
			padding: '0.5rem',
		},
		title: {
			fontSize: '1.5rem',
			margin: '1.5rem 0',
		},
		progressBarContainer: {
			margin: '0 1rem 0.75rem',
		},
		progressText: {
			marginBottom: '1rem',
		},
		tabContainer: {
			margin: '0 0.5rem 1rem',
		},
		tabContent: {
			padding: '1rem',
			margin: '0 0.5rem 1rem',
		},
		submitButton: {
			maxWidth: '100%',
			padding: '0.75rem 1rem',
			margin: '1rem auto',
		},
	},
});

export default HospitalProfile;
